/**
 * LocationMapModal
 *
 * Modal sob-demanda que mostra o pino exato da localização de um check-in
 * (Leaflet + tiles OpenStreetMap, sem necessidade de API key).
 *
 * Props:
 *   lat, lon        – coordenadas do check-in
 *   label           – texto legível do local (reverse-geocode), opcional
 *   dateLabel       – data/hora formatada do check-in, opcional
 *   checkinId       – id do UserExercise, opcional — sem ele o botão de editar
 *                      nome do local não aparece (não tem o que salvar)
 *   userId          – dono do check-in, exigido junto com checkinId pra editar
 *   canEditLabel    – bool, opcional (default false) — quem está vendo o modal
 *                      É professor/admin (nunca o próprio aluno participante:
 *                      a correção alimenta um cache reaproveitado por
 *                      proximidade em QUALQUER check-in futuro na mesma
 *                      coordenada, ver update_checkin_location_label em
 *                      app/api/users/checkins.py). Sem essa prop o botão de
 *                      editar fica oculto mesmo com checkinId/userId presentes.
 *   onLabelUpdated  – (novoLabel) => void, opcional — avisa o pai que o label
 *                      mudou (a lista por trás do modal não atualiza sozinha)
 *   onClose         – () => void
 */
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLocationDot, faUpRightFromSquare, faTriangleExclamation, faPen, faCheck, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./LocationMapModal.module.css";
import ModalHeader from "./ModalHeader";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import api from "../services/api";

// Ícone default do Leaflet aponta para assets que o bundler não resolve —
// substituímos por um marcador simples via divIcon, sem depender de imagens externas.
const pinIcon = L.divIcon({
  className: styles.pinIcon,
  html: `<span></span>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// O Leaflet calcula o tamanho do container no momento em que o mapa é
// inicializado. Dentro de um modal recém-montado, o layout flex às vezes
// ainda não estabilizou nesse instante e o mapa nasce com tiles em branco
// (0x0). No mobile isso piora: a barra de endereço/teclado some e volta,
// mudando a altura real da viewport (unidades dvh) depois do primeiro
// paint. Em vez de um único recálculo, observamos o próprio container e
// invalidamos o tamanho sempre que ele mudar — cobre mount, resize e
// mudanças de viewport do navegador mobile.
function InvalidateSizeOnMount() {
  const map = useMap();
  useEffect(() => {
    const container = map.getContainer();

    // Recálculos imediatos para cobrir o primeiro paint (várias tentativas
    // com delays crescentes, já que o layout do modal pode levar mais de
    // um frame para estabilizar em navegadores mobile).
    const timeouts = [0, 120, 350, 700].map((ms) =>
      setTimeout(() => map.invalidateSize(), ms)
    );

    // Recálculo contínuo enquanto o modal estiver aberto — cobre rotação
    // de tela e a barra de endereço do mobile aparecendo/sumindo. Debounced
    // porque invalidateSize() pode, por si só, disparar um novo ciclo do
    // observer — sem isso, em hardware mais lento vira uma rajada de
    // recálculos que trava a thread principal por um instante logo no mount.
    let debounceId = null;
    const observer = new ResizeObserver(() => {
      clearTimeout(debounceId);
      debounceId = setTimeout(() => map.invalidateSize(), 100);
    });
    observer.observe(container);

    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(debounceId);
      observer.disconnect();
    };
  }, [map]);
  return null;
}

// O mapa pode ficar visualmente em branco sem nenhum erro de JS quando os
// tiles simplesmente não carregam (rede do usuário, bloqueador de conteúdo
// no navegador mobile, etc.) — nesse caso não sobra nenhum sinal visível
// pro usuário nem pra nós. Escuta os eventos de tile do Leaflet e mostra um
// aviso explícito quando os tiles falham e nenhum chegou a carregar.
function TileStatusWatcher({ onStatusChange }) {
  useMapEvents({
    tileload: () => onStatusChange("loaded"),
    tileerror: () => onStatusChange("error"),
  });
  return null;
}

export default function LocationMapModal({ lat, lon, label, dateLabel, checkinId, userId, canEditLabel = false, onLabelUpdated, onClose }) {
  useBodyScrollLock(true);
  const [tileStatus, setTileStatus] = useState("loading"); // loading | loaded | error
  const [currentLabel, setCurrentLabel] = useState(label);
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(label || "");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  if (lat == null || lon == null) return null;
  const position = [lat, lon];
  const canEdit = !!checkinId && !!userId && canEditLabel;

  const handleTileStatus = (status) => {
    // Uma vez que algum tile carregou, o mapa está de fato visível —
    // erros isolados de tiles vizinhos não devem mais disparar o aviso.
    setTileStatus((prev) => (prev === "loaded" ? prev : status));
  };

  const startEditing = () => {
    setEditValue(currentLabel || "");
    setSaveError("");
    setEditing(true);
  };

  const saveLabel = async () => {
    const trimmed = editValue.trim();
    if (!trimmed) {
      setSaveError("Digite um nome.");
      return;
    }
    setSaving(true);
    setSaveError("");
    try {
      await api.patch(`/users/${userId}/checkins/${checkinId}/location-label`, { label: trimmed });
      setCurrentLabel(trimmed);
      onLabelUpdated?.(trimmed);
      setEditing(false);
    } catch (err) {
      setSaveError(err.response?.data?.detail || "Não foi possível salvar.");
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <ModalHeader onClose={onClose} />
        <div className={styles.header}>
          <span className={styles.icon}><FontAwesomeIcon icon={faLocationDot} /></span>
          <div className={styles.headerText}>
            {editing ? (
              <div className={styles.editRow}>
                <input
                  className={styles.editInput}
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="Nome do local (ex: Academia Fit)"
                  maxLength={200}
                  autoFocus
                  disabled={saving}
                  onKeyDown={(e) => { if (e.key === "Enter") saveLabel(); if (e.key === "Escape") setEditing(false); }}
                />
                <button type="button" className={styles.editBtn} onClick={saveLabel} disabled={saving} title="Salvar">
                  <FontAwesomeIcon icon={faCheck} />
                </button>
                <button type="button" className={styles.editBtn} onClick={() => setEditing(false)} disabled={saving} title="Cancelar">
                  <FontAwesomeIcon icon={faXmark} />
                </button>
              </div>
            ) : (
              <h2 className={styles.title}>
                {currentLabel || "Localização do check-in"}
                {canEdit && (
                  <button type="button" className={styles.editTrigger} onClick={startEditing} title="Corrigir nome do local">
                    <FontAwesomeIcon icon={faPen} />
                  </button>
                )}
              </h2>
            )}
            {dateLabel && !editing && <p className={styles.subtitle}>{dateLabel}</p>}
            {saveError && <p className={styles.editError}>{saveError}</p>}
          </div>
        </div>

        <div className={styles.mapWrap}>
          <MapContainer
            center={position}
            zoom={16}
            scrollWheelZoom={true}
            style={{ position: "absolute", inset: 0 }}
          >
            <InvalidateSizeOnMount />
            <TileStatusWatcher onStatusChange={handleTileStatus} />
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={position} icon={pinIcon}>
              {currentLabel && <Popup>{currentLabel}</Popup>}
            </Marker>
          </MapContainer>

          {tileStatus === "error" && (
            <div className={styles.tileErrorBanner}>
              <FontAwesomeIcon icon={faTriangleExclamation} />
              Não foi possível carregar o mapa. Verifique sua conexão ou use o link abaixo.
            </div>
          )}
        </div>

        <div className={styles.footer}>
          <span className={styles.coords}>{lat.toFixed(5)}, {lon.toFixed(5)}</span>
          <a
            className={styles.externalLink}
            href={`https://www.google.com/maps?q=${lat},${lon}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            Abrir no Google Maps
            <FontAwesomeIcon icon={faUpRightFromSquare} />
          </a>
        </div>
      </div>
    </div>,
    document.body
  );
}
